import type { MouseEventHandler } from 'react';

import IconButton from 'components/IconButton';
import { cn } from 'utils/classNames';
import { useI18n } from 'utils/i18n';
import { ENTITIES } from 'utils/string';

interface Props {
	className?: string;
	isPanDownDisabled?: boolean;
	isPanLeftDisabled?: boolean;
	isPanRightDisabled?: boolean;
	isPanUpDisabled?: boolean;
	isZoomInDisabled?: boolean;
	isZoomOutDisabled?: boolean;
	onPanDown: MouseEventHandler<HTMLButtonElement>;
	onPanLeft: MouseEventHandler<HTMLButtonElement>;
	onPanRight: MouseEventHandler<HTMLButtonElement>;
	onPanUp: MouseEventHandler<HTMLButtonElement>;
	onZoomIn: MouseEventHandler<HTMLButtonElement>;
	onZoomOut: MouseEventHandler<HTMLButtonElement>;
	zoomPercentage?: number;
}

export default function ImageZoomViewControls({
	className,
	isPanDownDisabled,
	isPanLeftDisabled,
	isPanRightDisabled,
	isPanUpDisabled,
	isZoomInDisabled,
	isZoomOutDisabled,
	onPanDown,
	onPanLeft,
	onPanRight,
	onPanUp,
	onZoomIn,
	onZoomOut,
	zoomPercentage,
}: Props) {
	const { t } = useI18n();
	const hasZoomPercentage = zoomPercentage !== undefined;

	return (
		<div className={cn('flex items-center justify-between gap-4', className)}>
			<div
				className={cn(
					'flex items-center',
					hasZoomPercentage ? 'gap-1' : 'gap-3',
				)}
			>
				<IconButton
					variant="secondary"
					icon="minus"
					onClick={onZoomOut}
					text={t('general_image_zoom_out_button_label')}
					disabled={isZoomOutDisabled}
				/>
				{hasZoomPercentage && (
					<span className="inline-block min-w-14 text-center">
						{zoomPercentage
							? `${Math.round(zoomPercentage)}%`
							: ENTITIES.noBreakSpace}
					</span>
				)}
				<IconButton
					variant="secondary"
					icon="plus"
					onClick={onZoomIn}
					text={t('general_image_zoom_in_button_label')}
					disabled={isZoomInDisabled}
				/>
			</div>

			<div className="flex items-center gap-1">
				<IconButton
					variant="secondary"
					icon="arrow-back"
					onClick={onPanLeft}
					text={t('general_image_pan_left_button_label')}
					disabled={isPanLeftDisabled}
				/>
				<div className="flex flex-col items-center gap-2">
					<IconButton
						variant="secondary"
						icon="arrow-up"
						onClick={onPanUp}
						text={t('general_image_pan_up_button_label')}
						disabled={isPanUpDisabled}
					/>
					<IconButton
						variant="secondary"
						icon="arrow-down"
						onClick={onPanDown}
						text={t('general_image_pan_down_button_label')}
						disabled={isPanDownDisabled}
					/>
				</div>
				<IconButton
					variant="secondary"
					icon="arrow-forward"
					onClick={onPanRight}
					text={t('general_image_pan_right_button_label')}
					disabled={isPanRightDisabled}
				/>
			</div>
		</div>
	);
}
ImageZoomViewControls.displayName = 'ImageZoomViewControls';
